/**
 * GPS Data Filtering Utilities
 * Provides functions for filtering GPS device data based on various criteria
 */

import type { DeviceGpsInfo } from '../../gps-websocket/types';

/**
 * GPS Data Subscriber interface
 */
interface GpsDataSubscriber {
  filter?: {
    psnList?: string[];
    activeOnly?: boolean;
    videoOnly?: boolean;
    boundingBox?: {
      sw: { lat: number; lng: number };
      ne: { lat: number; lng: number };
    };
    speedRange?: { min: number; max: number };
  };
}

/**
 * Apply basic auto-filtering to device data
 * @param devices - Raw device data
 * @param autoFilter - Auto-filter configuration
 * @returns Filtered device data
 */
export function applyAutoFilter(
  devices: DeviceGpsInfo[],
  autoFilter: {
    excludeInactive: boolean;
    excludeInvalidCoords: boolean;
    excludeStatic: boolean;
    minSpeedThreshold: number;
  }
): DeviceGpsInfo[] {
  
  return devices.filter(device => {
    // 1. 비활성 디바이스 제외
    if (autoFilter.excludeInactive && device.active !== 'on') {
      return false;
    }
    
    // 2. 잘못된 좌표 제외
    if (autoFilter.excludeInvalidCoords) {
      if (!isValidCoordinate(device.latitude, device.longitude)) {
        return false;
      }
    }
    
    // 3. 정적 디바이스 제외 (움직이지 않는 디바이스)
    if (autoFilter.excludeStatic) {
      const speed = device.speed || 0;
      if (speed < autoFilter.minSpeedThreshold) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Apply subscriber-specific filtering to device data
 * @param devices - Device data to filter
 * @param filter - Subscriber filter configuration
 * @returns Filtered device data
 */
export function applySubscriberFilter(
  devices: DeviceGpsInfo[],
  filter?: GpsDataSubscriber['filter']
): DeviceGpsInfo[] {
  
  if (!filter) return devices;
  
  return devices.filter(device => {
    // 1. PSN 리스트 필터
    if (filter.psnList && filter.psnList.length > 0) {
      if (!filter.psnList.includes(device.psn)) {
        return false;
      }
    }
    
    // 2. 활성 디바이스만 필터
    if (filter.activeOnly && device.active !== 'on') {
      return false;
    }
    
    // 3. 비디오 공유 디바이스만 필터
    if (filter.videoOnly && device.share_video !== 'on') {
      return false;
    }
    
    // 4. 경계 박스 필터
    if (filter.boundingBox) {
      if (!isWithinBoundingBox(device, filter.boundingBox)) {
        return false;
      }
    }
    
    // 5. 속도 범위 필터
    if (filter.speedRange) {
      const speed = device.speed || 0;
      if (speed < filter.speedRange.min || speed > filter.speedRange.max) {
        return false;
      }
    }
    
    return true;
  });
}

/**
 * Filter devices by status
 * @param devices - Device data
 * @param status - Status to filter by
 * @returns Filtered devices
 */
export function filterByStatus(
  devices: DeviceGpsInfo[],
  status: 'all' | 'active' | 'inactive' | 'video'
): DeviceGpsInfo[] {
  
  switch (status) {
    case 'active':
      return devices.filter(device => device.active === 'on');
    case 'inactive':
      return devices.filter(device => device.active !== 'on');
    case 'video':
      return devices.filter(device => device.share_video === 'on');
    case 'all':
    default:
      return devices;
  }
}

/**
 * Filter devices within a specific radius from a center point
 * @param devices - Device data
 * @param center - Center coordinate
 * @param radiusKm - Radius in kilometers
 * @returns Devices within radius
 */
export function filterByRadius(
  devices: DeviceGpsInfo[],
  center: { lat: number; lng: number },
  radiusKm: number
): DeviceGpsInfo[] {
  
  return devices.filter(device => {
    const distance = calculateDistance(
      center.lat, center.lng,
      device.latitude, device.longitude
    );
    return distance <= radiusKm * 1000; // Convert km to meters
  });
}

/**
 * Filter devices by speed range
 * @param devices - Device data
 * @param minSpeed - Minimum speed (km/h)
 * @param maxSpeed - Maximum speed (km/h)
 * @returns Devices within speed range
 */
export function filterBySpeed(
  devices: DeviceGpsInfo[],
  minSpeed: number,
  maxSpeed: number = Infinity
): DeviceGpsInfo[] {
  
  return devices.filter(device => {
    const speed = device.speed || 0;
    return speed >= minSpeed && speed <= maxSpeed;
  });
}

/**
 * Group devices by their properties
 * @param devices - Device data to group
 * @param groupBy - Property to group by
 * @returns Grouped devices
 */
export function groupDevices(
  devices: DeviceGpsInfo[],
  groupBy: 'status' | 'video' | 'speed' | 'region'
): Record<string, DeviceGpsInfo[]> {
  
  const groups: Record<string, DeviceGpsInfo[]> = {};
  
  devices.forEach(device => {
    let groupKey: string;
    
    switch (groupBy) {
      case 'status':
        groupKey = device.active === 'on' ? 'active' : 'inactive';
        break;
      case 'video':
        groupKey = device.share_video === 'on' ? 'sharing' : 'not-sharing';
        break;
      case 'speed':
        const speed = device.speed || 0;
        if (speed === 0) groupKey = 'stopped';
        else if (speed < 30) groupKey = 'slow';
        else if (speed < 60) groupKey = 'medium';
        else groupKey = 'fast';
        break;
      case 'region':
        groupKey = getRegionFromCoordinates(device.latitude, device.longitude);
        break;
      default:
        groupKey = 'unknown';
    }
    
    if (!groups[groupKey]) {
      groups[groupKey] = [];
    }
    groups[groupKey].push(device);
  });
  
  return groups;
}

/**
 * Sort devices by various criteria
 * @param devices - Device data to sort
 * @param sortBy - Sort criteria
 * @param ascending - Sort direction
 * @returns Sorted devices
 */
export function sortDevices(
  devices: DeviceGpsInfo[],
  sortBy: 'psn' | 'speed' | 'distance' | 'lastUpdate',
  ascending: boolean = true,
  referencePoint?: { lat: number; lng: number }
): DeviceGpsInfo[] {
  
  const sorted = [...devices].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'psn':
        comparison = a.psn.localeCompare(b.psn);
        break;
      case 'speed':
        comparison = (a.speed || 0) - (b.speed || 0);
        break;
      case 'distance':
        if (referencePoint) {
          const distA = calculateDistance(
            referencePoint.lat, referencePoint.lng,
            a.latitude, a.longitude
          );
          const distB = calculateDistance(
            referencePoint.lat, referencePoint.lng,
            b.latitude, b.longitude
          );
          comparison = distA - distB;
        }
        break;
      default:
        comparison = 0;
    }
    
    return ascending ? comparison : -comparison;
  });
  
  return sorted;
}

/**
 * Find devices near a specific device
 * @param devices - All device data
 * @param targetPsn - Target device PSN
 * @param radiusKm - Search radius in kilometers
 * @returns Devices near the target device
 */
export function findNearbyDevices(
  devices: DeviceGpsInfo[],
  targetPsn: string,
  radiusKm: number
): DeviceGpsInfo[] {
  
  const targetDevice = devices.find(device => device.psn === targetPsn);
  if (!targetDevice) return [];
  
  return filterByRadius(
    devices.filter(device => device.psn !== targetPsn),
    { lat: targetDevice.latitude, lng: targetDevice.longitude },
    radiusKm
  );
}

/**
 * Validate if coordinates are valid GPS coordinates
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns True if coordinates are valid
 */
export function isValidCoordinate(lat: number, lng: number): boolean {
  return !isNaN(lat) && 
         !isNaN(lng) && 
         isFinite(lat) && 
         isFinite(lng) &&
         lat >= -90 && 
         lat <= 90 &&
         lng >= -180 && 
         lng <= 180 &&
         !(lat === 0 && lng === 0); // Exclude null island
}

/**
 * Check if device is within bounding box
 * @param device - Device to check
 * @param boundingBox - Bounding box coordinates
 * @returns True if device is within bounds
 */
export function isWithinBoundingBox(
  device: DeviceGpsInfo,
  boundingBox: {
    sw: { lat: number; lng: number };
    ne: { lat: number; lng: number };
  }
): boolean {
  return device.latitude >= boundingBox.sw.lat &&
         device.latitude <= boundingBox.ne.lat &&
         device.longitude >= boundingBox.sw.lng &&
         device.longitude <= boundingBox.ne.lng;
}

/**
 * Calculate distance between two GPS coordinates using Haversine formula
 * @param lat1 - Latitude of first point
 * @param lng1 - Longitude of first point
 * @param lat2 - Latitude of second point
 * @param lng2 - Longitude of second point
 * @returns Distance in meters
 */
function calculateDistance(
  lat1: number, lng1: number, 
  lat2: number, lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Convert degrees to radians
 * @param degrees - Angle in degrees
 * @returns Angle in radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Get region name from coordinates (simplified)
 * @param lat - Latitude
 * @param lng - Longitude
 * @returns Region name
 */
function getRegionFromCoordinates(lat: number, lng: number): string {
  // 대한민국 주요 도시 기준 간단한 지역 분류
  if (lat >= 37.4 && lat <= 37.7 && lng >= 126.8 && lng <= 127.2) {
    return 'seoul';
  } else if (lat >= 35.0 && lat <= 35.3 && lng >= 128.9 && lng <= 129.3) {
    return 'busan';
  } else if (lat >= 35.8 && lat <= 36.0 && lng >= 128.5 && lng <= 128.8) {
    return 'daegu';
  } else if (lat >= 37.2 && lat <= 37.5 && lng >= 126.6 && lng <= 127.0) {
    return 'incheon';
  } else {
    return 'other';
  }
}

/**
 * Create a combined filter function from multiple filter criteria
 * @param filters - Array of filter functions
 * @returns Combined filter function
 */
export function createCombinedFilter(
  ...filters: Array<(device: DeviceGpsInfo) => boolean>
): (device: DeviceGpsInfo) => boolean {
  
  return (device: DeviceGpsInfo) => {
    return filters.every(filter => filter(device));
  };
}

/**
 * Create a filter function for device search
 * @param searchQuery - Search query string
 * @returns Filter function for search
 */
export function createSearchFilter(searchQuery: string): (device: DeviceGpsInfo) => boolean {
  const query = searchQuery.toLowerCase().trim();
  
  if (!query) {
    return () => true; // Return all devices if no search query
  }
  
  return (device: DeviceGpsInfo) => {
    // Search in PSN
    if (device.psn.toLowerCase().includes(query)) {
      return true;
    }
    
    // Search in device name
    if (device.dev_name && device.dev_name.toLowerCase().includes(query)) {
      return true;
    }
    
    return false;
  };
}